// DP: упрощённая форма прямой выплаты (без договора). Сумма + способ + реквизиты + комментарий.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createDirectPayment } from '../api/invoices.js';
import { usePacksByLead } from '../api/packs.js';
import { leadStatusToKindLabel } from '../lib/paymentBasis';
import MonthPicker from './MonthPicker';
import { queryKeys } from '../lib/queryKeys';
import { useToast } from '../contexts/ToastContext';
import DocForm from './DocForm.jsx';
import { packDisplayName } from '../lib/packName.js';

const METHODS = [
  { value: 'paypal', label: 'PayPal' },
  { value: 'crypto', label: 'Crypto' },
];

// Дефолт — ТЕКУЩИЙ календарный месяц (чиним прежний авто-«предыдущий месяц»).
function defaultMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function DirectPaymentModal({ lead, onClose }) {
  const qc = useQueryClient();
  const { addToast } = useToast();
  const [fee, setFee] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paymentAddress, setPaymentAddress] = useState('');
  const [comment, setComment] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const { data: packs = [], isLoading: packsLoading } = usePacksByLead(lead.id);
  const kindLabel = leadStatusToKindLabel(lead.statusId || lead.status);
  const packLabel = packDisplayName;

  const mutation = useMutation({
    mutationFn: () => createDirectPayment(lead.id, {
      fee: Number(fee),
      paymentMethod,
      paymentAddress: paymentAddress.trim(),
      periodMonth: selectedMonth,
      ...(selectedPackId ? { packId: selectedPackId } : {}),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contracts.byLead(lead.id) });
      qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all() });
      addToast('Готово', 'Прямая выплата создана', 'success');
      onClose();
    },
    onError: (err) => {
      addToast('Ошибка', err?.response?.data?.error || 'Не удалось создать выплату', 'error');
    },
  });

  const feeOk = fee !== '' && !Number.isNaN(Number(fee)) && Number(fee) > 0;
  const canSubmit = feeOk && paymentAddress.trim().length > 0 && !mutation.isPending;

  return (
    <DocForm
      title="Прямая выплата"
      meta={`Тип оплаты: ${kindLabel}`}
      onClose={onClose}
      closeDisabled={mutation.isPending}
      actions={(
        <>
          <button
            type="button"
            className="documents-action-btn"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Отмена
          </button>
          <button
            type="button"
            className="documents-action-btn documents-action-btn--primary"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
          >
            {mutation.isPending ? 'Создание…' : 'Создать выплату'}
          </button>
        </>
      )}
    >
      <p className="doc-form__hint">
        Фиксация выплаты без договора и документа. Появится в общем списке инвойсов
        с чипом «Прямая выплата».
      </p>

      <div className="doc-form__grid">
        <div className="contract-inline-form__field">
          <label htmlFor={`dp-pack-${lead.id}`}>Пачка</label>
          {packsLoading ? (
            <p className="doc-form__muted">Загрузка пачек…</p>
          ) : packs.length === 0 ? (
            <p className="doc-form__muted">У блогера нет пачек — выплата без привязки.</p>
          ) : (
            <select
              id={`dp-pack-${lead.id}`}
              value={selectedPackId ?? ''}
              onChange={(e) => setSelectedPackId(e.target.value || null)}
            >
              <option value="">— без пачки —</option>
              {packs.map((p) => (
                <option key={p.id} value={p.id}>{packLabel(p)}</option>
              ))}
            </select>
          )}
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`dp-month-${lead.id}`}>Месяц</label>
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`dp-fee-${lead.id}`}>Сумма, $</label>
          <input
            id={`dp-fee-${lead.id}`}
            type="number"
            min="0"
            step="0.01"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
          />
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`dp-method-${lead.id}`}>Способ оплаты</label>
          <select
            id={`dp-method-${lead.id}`}
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="contract-inline-form__field doc-form__span2">
          <label htmlFor={`dp-address-${lead.id}`}>Реквизиты (email PayPal или крипто-адрес)</label>
          <input
            id={`dp-address-${lead.id}`}
            type="text"
            value={paymentAddress}
            onChange={(e) => setPaymentAddress(e.target.value)}
          />
        </div>

        <div className="contract-inline-form__field contract-inline-form__field--full">
          <label htmlFor={`dp-comment-${lead.id}`}>Комментарий (необязательно — попадёт в историю статусов)</label>
          <input
            id={`dp-comment-${lead.id}`}
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
      </div>
    </DocForm>
  );
}
